/* eslint-disable camelcase */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isApp } from 'services/environment';
import { INTERCOM_GRACE_MS, reportIntercomMessage, reportReader } from 'services/interruptions';
import { useIntercomMayStart } from 'hooks/useInterruptions';
import IntercomForApp from './IntercomForApp';
import IntercomForWeb from './IntercomForWeb';

function Intercom() {
  const user = useSelector(state => state.user);
  const featureUsage = useSelector(state => state.featureUsage.usage);
  // The preference onboarding goes first, so Intercom starts once that has closed (or is not due).
  const mayStart = useIntercomMayStart(user.logged_in);

  // Say who the reader is, so that logging in or out inside the app starts the order afresh, with
  // its time limit, even on a page where the onboarding is not mounted.
  useEffect(
    () => {
      reportReader(!!user.logged_in);
    },
    [user.logged_in],
  );

  // Tell the coordinator when Intercom has something to show, so the NPS survey stays out of the way.
  // Only a logged-in reader is ever offered the survey, so only then is there anything to find out.
  const loggedIn = !!user.logged_in;
  useEffect(
    () => {
      if (!mayStart || !loggedIn) {
        return undefined;
      }
      let timer = null;
      try {
        if (isApp()) {
          // The app plugin has no event for messages, so ask once for the unread count, just before
          // the NPS survey would be allowed to load.
          timer = setTimeout(() => {
            try {
              if (
                window.intercom &&
                typeof window.intercom.unreadConversationCount === 'function'
              ) {
                window.intercom.unreadConversationCount(count => {
                  if (Number(count) > 0) {
                    reportIntercomMessage();
                  }
                });
              }
            } catch (error) {
              console.warn('Could not ask Intercom for the unread count', error);
            }
          }, Math.max(INTERCOM_GRACE_MS - 1000, 0));
        } else if (typeof window.Intercom === 'function') {
          window.Intercom('onShow', reportIntercomMessage);
          window.Intercom('onUnreadCountChange', count => {
            if (Number(count) > 0) {
              reportIntercomMessage();
            }
          });
        }
      } catch (error) {
        console.warn('Could not listen for Intercom messages', error);
      }
      return () => clearTimeout(timer);
    },
    [mayStart, loggedIn],
  );

  let userData = {};
  if (user.logged_in && user.id > 0) {
    userData = {
      email: user.email,
      name: user.display_name,
      user_id: user.id,
      membership_status: user.subscription_status,
    };

    Object.keys(featureUsage).forEach(key => {
      if (featureUsage[key] === true) {
        const featureFlag = {};
        featureFlag[key] = true;
        Object.assign(userData, featureFlag);
      }
    });

    if (user.subscription) {
      const {
        status,
        next_bill_date,
        cancellation_effective_date,
        is_valid,
        provider,
        billing_frequency,
        currency,
        coupon,
        next_payment_amount,
        description,
        partner,
        voucher_code,
        voucher_duration,
        voucher_used_at,
      } = user.subscription;

      Object.assign(userData, {
        sales_partner: partner,
        billing_frequency,
        next_payment_amount,
        coupon,
        currency,
        description,
        subscription_provider: provider,
        subscription_status: status,
        subscription_is_valid: is_valid,
        next_bill_at: next_bill_date ? next_bill_date.getTime() / 1000 : null,
        cancellation_effective_at: cancellation_effective_date
          ? cancellation_effective_date.getTime() / 1000
          : null,
        voucher: voucher_code,
        voucher_duration,
        voucher_used_at: voucher_used_at ? voucher_used_at.getTime() / 1000 : null,
      });
      if (typeof window !== 'undefined') {
        if (window.App.version !== '0.0.0') {
          Object.assign(userData, { app_version: window.App.version });
        }
        if (window.App.build !== '0') {
          Object.assign(userData, { app_build: window.App.build });
        }
      }
    }
  }

  if (!mayStart) {
    return null;
  }
  if (isApp()) {
    return <IntercomForApp userData={userData} loggedIn={user.logged_in} />;
  }
  return <IntercomForWeb userData={userData} loggedIn={user.logged_in} />;
}

export default Intercom;
